import React, { useState } from "react";
import { usePayments, useCreatePayment, useDeletePayment, useSetDefaultPayment, useUpdatePayment } from "@/lib/queries";
import { CreditCard, Lock, Plus, Trash2, Star, AlertTriangle, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono";
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

const EMPTY = {
  label: "",
  cardholder: "",
  number: "",
  cvv: "",
  exp_month: "",
  exp_year: "",
  billing_zip: "",
  is_default: false,
};

const BRAND_COLORS = {
  Visa: "#1A1F71",
  Mastercard: "#EB001B",
  Amex: "#2E77BC",
  Discover: "#FF6000",
  Card: "#A3A3A3",
};

export default function PaymentsPage() {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);  // when set, submit() does PATCH instead of POST
  const { data: payments = [] } = usePayments();
  const create = useCreatePayment();
  const update = useUpdatePayment();
  const del = useDeletePayment();
  const setDefault = useSetDefaultPayment();

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      label: p.label || "",
      cardholder: "",  // user re-types only if they want to change
      number: "",
      cvv: "",
      exp_month: String(p.exp_month || ""),
      exp_year: String(p.exp_year || ""),
      billing_zip: "",
      is_default: !!p.is_default,
    });
    // scroll the form into view
    setTimeout(() => document.querySelector('[data-testid="payments-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = (e) => {
    e.preventDefault();
    if (editingId) {
      // PATCH: card number / cvv / cardholder are optional (blank = keep existing)
      if (!form.label || !form.exp_month || !form.exp_year) {
        toast.error("Label and expiry are required");
        return;
      }
      update.mutate(
        {
          id: editingId,
          body: {
            ...form,
            exp_month: Number(form.exp_month),
            exp_year: Number(form.exp_year),
          },
        },
        {
          onSuccess: () => {
            toast.success("Card updated");
            cancelEdit();
          },
          onError: (e) => toast.error("Failed to update", { description: e?.response?.data?.detail }),
        }
      );
      return;
    }
    if (!form.label || !form.cardholder || !form.number || !form.cvv || !form.exp_month || !form.exp_year) {
      toast.error("All fields except billing zip are required");
      return;
    }
    create.mutate(
      {
        ...form,
        exp_month: Number(form.exp_month),
        exp_year: Number(form.exp_year),
      },
      {
        onSuccess: () => {
          toast.success("Card saved (encrypted)");
          setForm(EMPTY);
        },
        onError: (e) => toast.error("Failed to save", { description: e?.response?.data?.detail }),
      }
    );
  };

  return (
    <div className="p-6 max-w-6xl w-full mx-auto" data-testid="payments-page">
      <header className="mb-4">
        <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
          Payment <span className="text-[#EAB308]">methods</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Encrypted card vault (Fernet/AES-128). Used by scalp mode for simulated checkouts.
        </p>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-[#FBBF24] bg-[#FBBF24]/5 border border-[#FBBF24]/30 p-3 rounded-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong className="text-white">Personal/local use only.</strong> This is not PCI-DSS compliant — fine for a self-hosted desktop tool, not safe for sharing or production deployment. The mocked executor never actually charges this card.
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <form
          onSubmit={submit}
          className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm space-y-4"
          data-testid="payments-form"
        >
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
            {editingId ? (
              <>
                <Pencil className="w-4 h-4 text-[#3B82F6]" /> Edit card
                <button
                  type="button"
                  data-testid="payment-cancel-edit-btn"
                  onClick={cancelEdit}
                  className="ml-auto p-1 text-[#737373] hover:text-white"
                  title="Cancel edit"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <><Plus className="w-4 h-4 text-[#EAB308]" /> Add card</>
            )}
          </h2>
          {editingId && (
            <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/30 rounded-sm p-2.5 text-[11px] text-[#93C5FD] leading-relaxed" data-testid="payment-edit-hint">
              <strong className="text-white">Editing encrypted card.</strong> Cardholder / number / CVV / billing zip are <span className="text-[#3B82F6]">blank by design</span> — they&apos;re encrypted at rest and can&apos;t be re-shown. Leave them blank to keep current; only fill in what you want to change.
            </div>
          )}

          <div>
            <div className={LABEL}>Label{editingId ? "" : " *"}</div>
            <input
              data-testid="payment-label-input"
              className={INPUT}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Visa Main, Burner Mastercard"
            />
          </div>

          <div>
            <div className={LABEL}>Cardholder name{editingId ? "" : " *"}</div>
            <input
              data-testid="payment-cardholder-input"
              className={INPUT}
              value={form.cardholder}
              onChange={(e) => setForm({ ...form, cardholder: e.target.value })}
              placeholder={editingId ? "(unchanged — leave blank to keep current)" : "John Smith"}
            />
          </div>

          <div>
            <div className={LABEL}>Card number{editingId ? "" : " *"}</div>
            <input
              data-testid="payment-number-input"
              inputMode="numeric"
              className={INPUT}
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value.replace(/\s+/g, "") })}
              placeholder={editingId ? "(unchanged — leave blank to keep current)" : "4111 1111 1111 1111"}
              maxLength={19}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className={LABEL}>Exp MM{editingId ? "" : " *"}</div>
              <input
                data-testid="payment-exp-month-input"
                className={INPUT}
                value={form.exp_month}
                onChange={(e) => setForm({ ...form, exp_month: e.target.value })}
                placeholder="12"
                maxLength={2}
              />
            </div>
            <div>
              <div className={LABEL}>Exp YYYY{editingId ? "" : " *"}</div>
              <input
                data-testid="payment-exp-year-input"
                className={INPUT}
                value={form.exp_year}
                onChange={(e) => setForm({ ...form, exp_year: e.target.value })}
                placeholder="2028"
                maxLength={4}
              />
            </div>
            <div>
              <div className={LABEL}>CVV{editingId ? "" : " *"}</div>
              <input
                data-testid="payment-cvv-input"
                type="password"
                className={INPUT}
                value={form.cvv}
                onChange={(e) => setForm({ ...form, cvv: e.target.value })}
                placeholder={editingId ? "•••" : "•••"}
                maxLength={4}
              />
            </div>
          </div>

          <div>
            <div className={LABEL}>Billing post code (optional)</div>
            <input
              data-testid="payment-zip-input"
              className={INPUT}
              value={form.billing_zip}
              onChange={(e) => setForm({ ...form, billing_zip: e.target.value })}
              placeholder={editingId ? "(unchanged — leave blank to keep current)" : "2000"}
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
            <input
              data-testid="payment-default-checkbox"
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              className="w-3.5 h-3.5 accent-[#EAB308]"
            />
            Set as default
          </label>

          <button
            type="submit"
            data-testid="payment-submit-btn"
            disabled={create.isPending}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock className="w-4 h-4" /> {editingId ? "Save changes" : "Encrypt & save card"}
          </button>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Saved cards ({payments.length})
          </h2>
          {payments.length === 0 ? (
            <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#737373] rounded-sm text-sm">
              No cards yet.
            </div>
          ) : (
            payments.map((p) => {
              const color = BRAND_COLORS[p.brand] || BRAND_COLORS.Card;
              return (
                <div
                  key={p.id}
                  data-testid={`payment-item-${p.id}`}
                  className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#262626] p-4 rounded-sm card-hover"
                  style={{ borderTop: `2px solid ${color}` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-4 h-4" style={{ color }} />
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{p.brand}</span>
                        {p.is_default && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 border border-[#EAB308]/40 text-[#EAB308] bg-[#EAB308]/10 rounded-sm uppercase">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-lg text-white tracking-widest">
                        •••• •••• •••• {p.last4}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-[#A3A3A3]">
                        <span>{p.cardholder_masked}</span>
                        <span>{String(p.exp_month).padStart(2, "0")}/{String(p.exp_year).slice(-2)}</span>
                      </div>
                      <div className="text-[10px] text-[#737373] mt-1">{p.label}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!p.is_default && (
                        <button
                          data-testid={`payment-default-${p.id}`}
                          onClick={() =>
                            setDefault.mutate(p.id, { onSuccess: () => toast.success("Default updated") })
                          }
                          className="p-2 text-[#737373] hover:text-[#EAB308] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`payment-edit-${p.id}`}
                        onClick={() => startEdit(p)}
                        className="p-2 text-[#737373] hover:text-[#3B82F6] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                        title="Edit this card (number/cvv stay safely encrypted unless you retype them)"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`payment-delete-${p.id}`}
                        onClick={() =>
                          del.mutate(p.id, { onSuccess: () => toast.success("Card removed") })
                        }
                        className="p-2 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
